import React, { useState, useEffect, useRef } from 'react';
import { productosAPI, ventasAPI, clientesAPI, cajaAPI } from '../api/client';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { Search, Plus, Trash2, ShoppingCart, Printer, Camera, CreditCard, X, MessageCircle, User, Zap, AlertTriangle } from 'lucide-react';
import BarcodeScannerNew from '../components/BarcodeScannerNew';
import TicketPrinter from '../components/TicketPrinter';

const VentasPage = () => {
  const [productos, setProductos] = useState([]);
  const [todosLosProductos, setTodosLosProductos] = useState([]); // Todos los productos para búsqueda
  const [carrito, setCarrito] = useState(() => {
    // Cargar carrito desde localStorage al iniciar
    try {
      const savedCarrito = localStorage.getItem('eleventa_carrito');
      return savedCarrito ? JSON.parse(savedCarrito) : [];
    } catch (error) {
      console.error('Error al cargar carrito:', error);
      return [];
    }
  });
  const [busqueda, setBusqueda] = useState('');
  const [codigoBarras, setCodigoBarras] = useState('');
  const [cliente, setCliente] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [total, setTotal] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [totalDescuentos, setTotalDescuentos] = useState(0);
  const [impuestos, setImpuestos] = useState(0);
  const [ultimaVenta, setUltimaVenta] = useState(null);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [showNuevoCliente, setShowNuevoCliente] = useState(false);
  const [showSelectorCliente, setShowSelectorCliente] = useState(false);
  const [showConfirmarCredito, setShowConfirmarCredito] = useState(false);
  const [showVentaRapidaModal, setShowVentaRapidaModal] = useState(false);
  const [nuevoCliente, setNuevoCliente] = useState({
    nombre: '',
    telefono: '',
    direccion: ''
  });
  const [ventaRapidaData, setVentaRapidaData] = useState({
    descripcion: '',
    precio: ''
  });
  const [metodoPago, setMetodoPago] = useState('contado'); // 'contado' o 'credito'
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [montoRecibido, setMontoRecibido] = useState('');
  const [cambio, setCambio] = useState(0);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [isMayorista, setIsMayorista] = useState(false);
  const [cajaAbierta, setCajaAbierta] = useState(null);
  const [showAlertCaja, setShowAlertCaja] = useState(false);
  const [ultimoProductoAgregado, setUltimoProductoAgregado] = useState(null);
  const searchInputRef = useRef(null);
  const barcodeInputRef = useRef(null);
  const { formatCurrency, config } = useConfig();
  const { user } = useAuth();

  useEffect(() => {
    loadProductos();
    loadClientes();
    verificarCajaAbierta();
    loadTodosLosProductos(); // Cargar todos los productos para búsqueda
  }, []);

  useEffect(() => {
    calcularTotal();
    // Guardar carrito en localStorage cada vez que cambie
    try {
      localStorage.setItem('eleventa_carrito', JSON.stringify(carrito));
    } catch (error) {
      console.error('Error al guardar carrito:', error);
    }
  }, [carrito]);

  const loadTodosLosProductos = async () => {
    try {
      const response = await productosAPI.getAll();
      if (response.data.success) {
        setTodosLosProductos(response.data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar productos:', error);
    }
  };

  const loadProductos = async () => {
    try {
      const response = await productosAPI.getAll();
      if (response.data.success) {
        setProductos(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar productos:', error);
    }
  };

  const loadClientes = async () => {
    try {
      const response = await clientesAPI.getAll();
      if (response.data.success) {
        setClientes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      setClientes([]);
    }
  };

  const verificarCajaAbierta = async () => {
    try {
      const response = await cajaAPI.getActual();
      if (response.data.success && response.data.data) {
        setCajaAbierta(response.data.data);
        setShowAlertCaja(false);
      } else {
        setCajaAbierta(null);
        setShowAlertCaja(true);
      }
    } catch (error) {
      console.error('Error al verificar caja:', error);
      setCajaAbierta(null);
      setShowAlertCaja(true);
    }
  };

  const guardarNuevoCliente = async () => {
    if (!nuevoCliente.nombre.trim()) {
      alert('El nombre del cliente es obligatorio');
      return;
    }

    try {
      const response = await clientesAPI.create(nuevoCliente);
      if (response.data.success) {
        const clienteCreado = response.data.data;
        setCliente(clienteCreado);
        setClientes([...clientes, clienteCreado]);
        setShowNuevoCliente(false);
        setNuevoCliente({
          nombre: '',
          telefono: '',
          direccion: ''
        });
        alert('Cliente creado exitosamente');
      }
    } catch (error) {
      console.error('Error al crear cliente:', error);
      alert('Error al crear cliente: ' + (error.response?.data?.message || error.message));
    }
  };

  const buscarProducto = async (codigo = null) => {
    const busquedaTexto = (codigo || busqueda).trim();
    if (!busquedaTexto) return;

    try {
      console.log('Buscando producto por nombre:', busquedaTexto);
      const searchResponse = await productosAPI.search(busquedaTexto);
      
      if (searchResponse.data.success && searchResponse.data.data) {
        if (Array.isArray(searchResponse.data.data)) {
          if (searchResponse.data.data.length === 1) {
            agregarAlCarrito(searchResponse.data.data[0]);
            setBusqueda('');
            focusSearchInput();
          } else if (searchResponse.data.data.length > 0) {
            // Mostrar productos encontrados SIN ALERTA
            setProductos(searchResponse.data.data);
            console.log(`${searchResponse.data.data.length} productos encontrados`);
          } else {
            // Solo mostrar en consola, no alertar
            console.log('No se encontraron productos con ese nombre');
            setProductos([]); // Limpiar lista
          }
        } else {
          agregarAlCarrito(searchResponse.data.data);
          setBusqueda('');
          focusSearchInput();
        }
      } else {
        console.log(`No se encontró ningún producto con: ${busquedaTexto}`);
        setProductos([]); // Limpiar lista
      }
    } catch (error) {
      console.error('Error al buscar producto:', error);
      // Solo mostrar error crítico en consola
      setProductos([]); // Limpiar lista en caso de error
    } finally {
      setBusqueda('');
      focusSearchInput();
    }
  };

  const buscarPorCodigoBarras = async (codigo = null) => {
    const codigoBuscar = (codigo || codigoBarras).trim();
    if (!codigoBuscar) return;
    
    // Evitar búsquedas duplicadas
    if (window.ultimaBúsquedaCodigo === codigoBuscar && 
        Date.now() - window.ultimaBúsquedaTiempo < 1000) {
      console.log('Búsqueda duplicada ignorada:', codigoBuscar);
      return;
    }
    window.ultimaBúsquedaCodigo = codigoBuscar;
    window.ultimaBúsquedaTiempo = Date.now();

    try {
      console.log('Buscando producto con código de barras:', codigoBuscar);
      
      // Buscar por código de barras exacto
      try {
        const response = await productosAPI.getByCodigo(codigoBuscar);
          if (response.data.success && response.data.data) {
            console.log('Producto encontrado por código:', response.data.data);
            agregarAlCarrito(response.data.data);
            setCodigoBarras(''); // Limpiar código después de agregar
            focusBarcodeInput(); // Volver el foco al campo de código
            return;
          }
      } catch (err) {
        console.log('Código de barras no encontrado:', codigoBuscar);
        // No mostrar alerta, solo limpiar el campo para permitir otro intento
      }
    } catch (error) {
      console.error('Error al buscar producto por código:', error);
      // No mostrar alerta de error
    } finally {
      setCodigoBarras('');
      focusBarcodeInput();
    }
  };

  const handleBarcodeScan = (code) => {
    console.log('Código escaneado:', code);
    setIsScannerOpen(false);
    buscarPorCodigoBarras(code);
  };
  
  const handleBarcodeDetected = handleBarcodeScan;

  // Función helper para manejar el foco inteligentemente
  const focusSearchInput = () => {
    // Solo hacer auto-focus si NO es un dispositivo móvil (ancho > 768px)
    if (window.innerWidth > 768 && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
        searchInputRef.current.select();
      }, 100);
    }
  };

  const focusBarcodeInput = () => {
    // Mantener el foco en el campo de código de barras para escáner USB
    if (window.innerWidth > 768 && barcodeInputRef.current) {
      setTimeout(() => {
        barcodeInputRef.current.focus();
        barcodeInputRef.current.select();
      }, 100);
    }
  };

  // Foco inicial al cargar - enfocado en el campo de código de barras
  useEffect(() => {
    focusBarcodeInput();
    
    // Inicializar variables para evitar búsquedas duplicadas
    window.ultimaBúsquedaCodigo = '';
    window.ultimaBúsquedaTiempo = 0;
    
    // Mantener el foco en el campo de código de barras para escáner USB
    const handleFocusBack = (e) => {
      // Si el foco no está en ningún input específico, volver al campo de código
      if (window.innerWidth > 768 && 
          !e.target.matches('input, textarea, select, button') && 
          barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    };
    
    // Detectar cuando el documento pierde el foco y restaurarlo
    const handleWindowBlur = () => {
      if (barcodeInputRef.current && window.innerWidth > 768) {
        setTimeout(() => {
          barcodeInputRef.current.focus();
        }, 10);
      }
    };
    
    document.addEventListener('click', handleFocusBack);
    window.addEventListener('blur', handleWindowBlur);
    
    // Intervalo para asegurar que el campo de código siempre tenga foco
    const focusInterval = setInterval(() => {
      if (window.innerWidth > 768 && 
          barcodeInputRef.current && 
          document.activeElement !== barcodeInputRef.current &&
          document.activeElement !== searchInputRef.current &&
          !document.activeElement.matches('input, textarea, select')) {
        barcodeInputRef.current.focus();
      }
    }, 500);
    
    return () => {
      document.removeEventListener('click', handleFocusBack);
      window.removeEventListener('blur', handleWindowBlur);
      clearInterval(focusInterval);
      clearTimeout(window.barcodeTimeout);
    };
  }, []);

  // Manejador de teclas especiales
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F12 o Enter (cuando no está en un input) para cobrar
      if ((e.key === 'F12' || (e.key === 'Enter' && !e.target.matches('input, textarea'))) && carrito.length > 0) {
        e.preventDefault();
        handlePagoContado();
      }
      // F9 para venta rápida
      else if (e.key === 'F9') {
        e.preventDefault();
        setShowVentaRapidaModal(true);
      }
      // Ctrl+P para imprimir último ticket
      else if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        if (ultimaVenta) {
          const printer = new TicketPrinter({
            nombreNegocio: config?.nombre_negocio || 'Mi Tienda',
            direccion: config?.direccion || '',
            telefono: config?.telefono || '',
            rfc: config?.rfc || ''
          });
          printer.print(ultimaVenta);
        }
      }
      // Tecla + para agregar más del último producto
      else if ((e.key === '+' || e.key === '=') && ultimoProductoAgregado) {
        e.preventDefault();
        agregarAlCarrito(ultimoProductoAgregado);
      }
      // Esc para cerrar modales
      else if (e.key === 'Escape') {
        setShowPrintOptions(false);
        setShowVentaRapidaModal(false);
        setShowPagoModal(false);
        setShowSelectorCliente(false);
        setShowNuevoCliente(false);
        setShowConfirmarCredito(false);
        focusBarcodeInput();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [carrito, ultimaVenta, ultimoProductoAgregado, config]);

  const agregarAlCarrito = (producto) => {
    // Guardar el último producto agregado para la tecla +
    setUltimoProductoAgregado(producto);
    // Validar stock si no es venta rápida (producto con ID real)
    if (producto.id && !producto.id.toString().startsWith('temp_')) {
      const stockDisponible = parseFloat(producto.stock_total || 0);
      
      // Solo validar que haya stock disponible
      if (stockDisponible <= 0) {
        alert(`❌ No hay stock disponible para ${producto.nombre}`);
        return;
      }
      
      // Buscar cantidad ya en carrito
      const enCarrito = carrito.find(item => item.id === producto.id);
      const cantidadEnCarrito = enCarrito ? enCarrito.cantidad : 0;
      
      // BLOQUEAR si se está por exceder el stock (sin opción de continuar)
      if (cantidadEnCarrito + 1 > stockDisponible) {
        alert(`❌ Stock insuficiente\n\n` +
              `Disponible: ${stockDisponible} unidades\n` +
              `En carrito: ${cantidadEnCarrito} unidades\n` +
              `No puedes agregar más de lo disponible`);
        return;
      }
    }

    setCarrito(prevCarrito => {
      const existe = prevCarrito.find(item => item.id === producto.id);
      const precioAplicar = isMayorista && producto.precio_mayoreo > 0 ? producto.precio_mayoreo : producto.precio_venta;
      
      if (existe) {
        return prevCarrito.map(item =>
          item.id === producto.id
            ? { ...item, cantidad: item.cantidad + 1, precio_unitario: parseFloat(precioAplicar) }
            : item
        );
      } else {
        return [...prevCarrito, {
          ...producto,
          cantidad: 1,
          precio_unitario: parseFloat(precioAplicar),
          descuento: 0,
          descuento_monto: 0
        }];
      }
    });
    
    // Volver el foco al input de búsqueda (solo desktop)
    focusSearchInput();
  };

  // Efecto para recalcular precios si cambia modo mayorista
  useEffect(() => {
    setCarrito(prevCarrito => prevCarrito.map(item => {
      // Solo actualizar items que son productos reales (tienen stock_total definidos o son de BD)
      if (!item.id.toString().startsWith('temp_') && item.precio_mayoreo) {
        const nuevoPrecio = isMayorista && item.precio_mayoreo > 0 ? item.precio_mayoreo : item.precio_venta;
        return { ...item, precio_unitario: parseFloat(nuevoPrecio) };
      }
      return item;
    }));
  }, [isMayorista]);

  const eliminarDelCarrito = (id) => {
    setCarrito(prevCarrito => prevCarrito.filter(item => item.id !== id));
  };

  const actualizarCantidad = (id, cantidad) => {
    if (cantidad <= 0) {
      eliminarDelCarrito(id);
      return;
    }

    // Buscar el producto en el carrito
    const producto = carrito.find(item => item.id === id);
    if (!producto) return;

    // Validar stock si no es venta rápida
    if (producto.id && !producto.id.toString().startsWith('temp_')) {
      const stockDisponible = parseFloat(producto.stock_total || 0);
      const nuevaCantidad = parseFloat(cantidad);
      
      // BLOQUEAR si excede el stock disponible
      if (nuevaCantidad > stockDisponible) {
        alert(`❌ Stock insuficiente\n\n` +
              `Disponible: ${stockDisponible} unidades\n` +
              `Intentaste poner: ${nuevaCantidad} unidades\n` +
              `No puedes vender más de lo disponible`);
        return;
      }
    }

    setCarrito(prevCarrito => prevCarrito.map(item =>
      item.id === id ? { ...item, cantidad: parseFloat(cantidad) } : item
    ));
  };

  const toggleMayoreoItem = (id) => {
    setCarrito(prevCarrito => prevCarrito.map(item => {
      if (item.id === id) {
        // Si ya tenía precio manual de mayoreo aplicado, lo quitamos.
        // O si queremos cambiar entre precio venta y precio mayoreo.
        const esMayoreoActual = Math.abs(item.precio_unitario - parseFloat(item.precio_mayoreo)) < 0.01;
        // Si ya es mayoreo, volvemos a normal. Si no, aplicamos mayoreo.
        const nuevoPrecio = esMayoreoActual ? item.precio_venta : (item.precio_mayoreo > 0 ? item.precio_mayoreo : item.precio_venta);
        
        return { ...item, precio_unitario: parseFloat(nuevoPrecio) };
      }
      return item;
    }));
  };

  const actualizarDescuento = (id, descuentoMonto) => {
    setCarrito(prevCarrito => prevCarrito.map(item => {
      if (item.id === id) {
        const subtotalItem = item.precio_unitario * item.cantidad;
        const monto = Math.max(0, Math.min(subtotalItem, parseFloat(descuentoMonto) || 0));
        return {
          ...item,
          descuento_monto: monto
        };
      }
      return item;
    }));
  };

  const calcularTotal = () => {
    const sub = carrito.reduce((sum, item) => 
      sum + (item.precio_unitario * item.cantidad), 0
    );
    const descuentos = carrito.reduce((sum, item) => 
      sum + (item.descuento_monto || 0), 0
    );
    
    const subtotalConDescuento = sub - descuentos;
    
    // Calcular impuestos si están activados
    let impuestosCalculados = 0;
    if (config?.usar_impuestos) {
      const porcentaje = parseFloat(config.porcentaje_iva) || 0;
      impuestosCalculados = subtotalConDescuento * (porcentaje / 100);
    }
    
    setSubtotal(sub);
    setTotalDescuentos(descuentos);
    setImpuestos(impuestosCalculados);
    setTotal(subtotalConDescuento + impuestosCalculados);
  };

  const handlePagoContado = () => {
    setMontoRecibido('');
    setCambio(0);
    setShowPagoModal(true);
  };

  const procesarVenta = async (esCredito = false) => {
    // Verificar que haya caja abierta
    if (!cajaAbierta) {
      alert('⚠️ No hay caja abierta.\n\nPor favor dirígete al módulo de Caja y abre una caja antes de realizar ventas.');
      return;
    }

    if (carrito.length === 0) {
      alert('El carrito está vacío');
      return;
    }

    if (esCredito && !cliente) {
      setShowSelectorCliente(true);
      return;
    }

    const venta = {
      cliente_id: cliente?.id || null,
      vendedor_id: user?.id || 1,
      usuario_nombre: user?.username || 'Sistema',
      sucursal_id: user?.sucursal_id || 1,
      almacen_id: 1,
      detalle: carrito.map(item => ({
        producto_id: item.id,
        producto_nombre: item.nombre,
        cantidad: item.cantidad,
        precio_unitario: item.precio_unitario,
        descuento: item.descuento || 0,
        descuento_monto: item.descuento_monto || 0,
        costo_unitario: item.costo_promedio || 0
      })),
      subtotal: subtotal,
      descuento: totalDescuentos,
      impuestos: impuestos,
      total: total,
      forma_pago: esCredito ? 'credito' : 'efectivo',
      metodo_pago: esCredito ? 'Crédito' : 'Efectivo',
      fecha_venta: new Date().toISOString(),
      es_credito: esCredito,
      dias_credito: esCredito ? 30 : 0,
      pago_efectivo: parseFloat(montoRecibido) || 0,
      cambio: cambio || 0
    };

    try {
      console.log('Enviando venta:', venta);
      const response = await ventasAPI.create(venta);
      console.log('Respuesta:', response);
      
      if (response.data.success) {
        const ventaData = {
          ...venta,
          id: response.data.data?.id || Date.now(),
          folio: response.data.data?.folio || `V${Date.now()}`
        };
        setUltimaVenta(ventaData);
        
        // Mostrar opciones de impresión
        setShowPrintOptions(true);
        
        setCarrito([]);
        localStorage.removeItem('eleventa_carrito'); // Limpiar carrito guardado
        setCliente(null);
        setBusquedaCliente('');
        setShowPagoModal(false); // Cerrar modal si estaba abierto
        setMontoRecibido('');
        setCambio(0);
        
        // Volver foco al input para siguiente venta
        focusSearchInput();
      } else {
        alert('Error al procesar venta: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error completo:', error);
      console.error('Error response:', error.response);
      
      let mensajeError = 'Error al procesar venta';
      
      if (error.response) {
        // El servidor respondió con un código de error
        if (error.response.status === 404) {
          mensajeError = 'Error: Endpoint de ventas no encontrado. Verifica que el archivo ventas.php exista en el servidor.';
        } else {
          mensajeError = error.response.data?.message || `Error ${error.response.status}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        // La petición fue hecha pero no hubo respuesta
        mensajeError = 'Error: No se pudo conectar con el servidor. Verifica tu conexión.';
      } else {
        // Algo pasó al configurar la petición
        mensajeError = error.message;
      }
      
      alert(mensajeError);
    }
  };

  const imprimirTicket = () => {
    if (!ultimaVenta) {
      alert('No hay venta para imprimir');
      return;
    }
    
    const printer = new TicketPrinter({
      nombreNegocio: config?.nombre_negocio || 'Mi Tienda',
      direccion: config?.direccion || '',
      telefono: config?.telefono || '',
      rfc: config?.rfc || ''
    });
    
    printer.print(ultimaVenta);
  };

  const descargarTicketAuto = (venta) => {
    if (!venta) return;
    
    const printer = new TicketPrinter({
      nombreNegocio: config?.nombre_negocio || 'Mi Tienda',
      direccion: config?.direccion || '',
      telefono: config?.telefono || '',
      rfc: config?.rfc || ''
    });
    
    printer.download(venta);
  };

  const compartirWhatsApp = () => {
    if (!ultimaVenta) {
      alert('No hay venta para compartir');
      return;
    }

    const mensaje = generarMensajeWhatsApp(ultimaVenta);
    const numeroCliente = cliente?.telefono?.replace(/[^0-9]/g, '') || '';
    
    // Si hay número de cliente, abrir chat directo
    if (numeroCliente) {
      const url = `https://wa.me/${numeroCliente}?text=${encodeURIComponent(mensaje)}`;
      window.open(url, '_blank');
    } else {
      // Sin número, abrir WhatsApp con el mensaje listo para compartir
      const url = `https://wa.me/?text=${encodeURIComponent(mensaje)}`;
      window.open(url, '_blank');
    }
  };

  const generarMensajeWhatsApp = (venta) => {
    const nombreNegocio = config?.nombre_negocio || 'Mi Tienda';
    const fecha = new Date(venta.fecha_venta).toLocaleString('es-GT');
    
    let mensaje = `🧾 *TICKET DE VENTA*\n`;
    mensaje += `━━━━━━━━━━━━━━━━━━\n\n`;
    mensaje += `📍 *${nombreNegocio}*\n`;
    mensaje += `📅 ${fecha}\n`;
    mensaje += `🔖 Folio: *${venta.folio}*\n\n`;
    
    if (cliente) {
      mensaje += `👤 Cliente: ${cliente.nombre}\n\n`;
    }
    
    mensaje += `*PRODUCTOS:*\n`;
    mensaje += `━━━━━━━━━━━━━━━━━━\n`;
    
    venta.detalle.forEach(item => {
      const subtotalItem = (item.precio_unitario * item.cantidad) - (item.descuento_monto || 0);
      mensaje += `\n${item.producto_nombre}\n`;
      mensaje += `  ${item.cantidad} x ${formatCurrency(item.precio_unitario)}`;
      if (item.descuento_monto > 0) {
        mensaje += ` (-${formatCurrency(item.descuento_monto)})`;
      }
      mensaje += ` = ${formatCurrency(subtotalItem)}\n`;
    });
    
    mensaje += `\n━━━━━━━━━━━━━━━━━━\n`;
    mensaje += `Subtotal: ${formatCurrency(venta.subtotal)}\n`;
    
    if (venta.descuento > 0) {
      mensaje += `Descuento: -${formatCurrency(venta.descuento)}\n`;
    }
    
    if (venta.impuestos > 0) {
      mensaje += `IVA: +${formatCurrency(venta.impuestos)}\n`;
    }
    
    mensaje += `\n💰 *TOTAL: ${formatCurrency(venta.total)}*\n`;
    mensaje += `━━━━━━━━━━━━━━━━━━\n\n`;
    mensaje += `💳 Forma de pago: *${venta.metodo_pago}*\n\n`;
    mensaje += `¡Gracias por su compra! 🙏`;
    
    return mensaje;
  };

  const agregarVentaRapida = () => {
    if (!ventaRapidaData.descripcion.trim() || !ventaRapidaData.precio || parseFloat(ventaRapidaData.precio) <= 0) {
      alert('Por favor ingrese una descripción y un precio válido');
      return;
    }

    const nuevoItem = {
      id: `temp_${Date.now()}`, // ID temporal
      producto_id: null, // Importante: null para indicar que no es de inventario
      nombre: ventaRapidaData.descripcion,
      precio_venta: parseFloat(ventaRapidaData.precio),
      precio_unitario: parseFloat(ventaRapidaData.precio),
      costo_promedio: 0,
      cantidad: 1,
      stock_total: null, // No hay stock que controlar
      descuento: 0,
      descuento_monto: 0
    };

    setCarrito([...carrito, nuevoItem]);
    setShowVentaRapidaModal(false);
    setVentaRapidaData({ descripcion: '', precio: '' });
    
    focusSearchInput();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      {/* Alerta de Caja Cerrada */}
      {showAlertCaja && (
        <div className="max-w-7xl mx-auto mb-4">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg shadow-sm">
            <div className="flex items-center">
              <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  No hay caja abierta
                </h3>
                <p className="mt-1 text-sm text-yellow-700">
                  Debes abrir una caja antes de poder realizar ventas. 
                  <a href="/caja" className="underline font-semibold hover:text-yellow-900">
                    Ir al módulo de Caja
                  </a>
                </p>
              </div>
              <button
                onClick={() => setShowAlertCaja(false)}
                className="ml-4 text-yellow-500 hover:text-yellow-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header - Responsive */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10 px-4 py-3 md:px-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-gray-900">💳 Punto de Venta</h1>
            <p className="text-xs md:text-sm text-gray-500 mt-1">
              {carrito.length} {carrito.length === 1 ? 'producto' : 'productos'} en carrito
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:block text-right">
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-lg font-bold text-primary-600">{formatCurrency(total)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto px-2 md:px-4 lg:px-6 mt-4">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 md:gap-6">
          {/* Panel de productos (Izquierda) */}
          <div className="flex flex-col h-[calc(100vh-200px)]">
            
            {/* Barra de Herramientas Compacta */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 mb-3">
              <div className="flex flex-col gap-2">
                {/* Campo para Código de Barras (Escáner USB) */}
                <div className="flex gap-2">
                  <div className="relative flex-grow">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2m-2 0a1 1 0 011-1h12a1 1 0 011 1M5 8v10a1 1 0 001 1h12a1 1 0 001-1V8M5 8l1-1m-1 1l1 1" />
                      </svg>
                    </div>
                    <input
                      ref={barcodeInputRef}
                      type="text"
                      value={codigoBarras}
                      onChange={(e) => {
                        const value = e.target.value;
                        const previousValue = codigoBarras;
                        setCodigoBarras(value);
                        
                        // Auto-buscar INMEDIATAMENTE cuando se detecta un patrón de código completo
                        // Los escáneres USB envían todos los caracteres de golpe
                        if (value.length > previousValue.length) { // Solo en entrada, no en borrado
                          // Para códigos numéricos de 8+ dígitos
                          if (value.length >= 8 && /^\d+$/.test(value)) {
                            console.log('Código numérico detectado, buscando inmediatamente:', value);
                            buscarPorCodigoBarras(value);
                          }
                          // Para cualquier código de 6+ caracteres (puede incluir letras)
                          else if (value.length >= 6 && value.length - previousValue.length > 3) {
                            // Si se agregaron más de 3 caracteres de golpe, es un escáner
                            console.log('Código de escáner detectado, buscando inmediatamente:', value);
                            buscarPorCodigoBarras(value);
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        // Enter manual siempre funciona
                        if (e.key === 'Enter' || e.keyCode === 13) {
                          e.preventDefault();
                          e.stopPropagation();
                          if (codigoBarras.trim()) {
                            console.log('Enter manual detectado, buscando:', codigoBarras);
                            buscarPorCodigoBarras();
                          }
                        }
                      }}
                      onPaste={(e) => {
                        const pastedText = e.clipboardData.getData('text');
                        if (pastedText) {
                          e.preventDefault();
                          setCodigoBarras(pastedText);
                          setTimeout(() => buscarPorCodigoBarras(pastedText), 10);
                        }
                      }}
                      placeholder="🔍 Escanear código de barras..."
                      className="block w-full pl-10 pr-3 py-2 border-2 border-blue-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50"
                      tabIndex="1"
                      autoFocus
                    />
                  </div>
                  <button 
                    onClick={() => setIsScannerOpen(true)} 
                    className="px-3 py-2 border border-gray-300 text-sm rounded-lg text-gray-700 bg-white hover:bg-gray-50"
                    title="Usar cámara"
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Campo para Búsqueda por Nombre */}
                <div className="flex gap-2">
                  <div className="relative flex-grow">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={busqueda}
                      onChange={(e) => {
                        const valor = e.target.value;
                        setBusqueda(valor);
                        // Filtrar productos en tiempo real como en ProductosPage
                        if (valor.trim()) {
                          const filtrados = todosLosProductos.filter(producto =>
                            producto.nombre.toLowerCase().includes(valor.toLowerCase()) ||
                            producto.sku?.toLowerCase().includes(valor.toLowerCase()) ||
                            producto.codigo_barras?.toLowerCase().includes(valor.toLowerCase())
                          ).slice(0, 10); // Limitar a 10 resultados
                          setProductos(filtrados);
                        } else {
                          setProductos([]);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && productos.length > 0) {
                          e.preventDefault();
                          // Agregar el primer producto de la lista
                          agregarAlCarrito(productos[0]);
                          setBusqueda('');
                          setProductos([]);
                          focusSearchInput();
                        }
                      }}
                      placeholder="🔤 Buscar por nombre del producto..."
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      tabIndex="2"
                    />
                  </div>

                  {/* Botones de Acción Compactos */}
                  <button 
                    onClick={() => setShowVentaRapidaModal(true)} 
                    className="px-3 py-2 text-sm rounded-lg text-white bg-purple-600 hover:bg-purple-700"
                    title="Venta Rápida"
                  >
                    <Zap className="h-4 w-4" />
                  </button>
                  
                  <button
                    onClick={() => setIsMayorista(!isMayorista)}
                    className={`px-3 py-2 text-sm rounded-lg ${
                      isMayorista 
                        ? 'text-yellow-700 bg-yellow-50 border border-yellow-400' 
                        : 'text-gray-700 bg-white border border-gray-300'
                    }`}
                    title={isMayorista ? "Mayoreo ON" : "Mayoreo OFF"}
                  >
                    <span className="font-bold">$$</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Lista de Productos */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm flex-grow overflow-hidden flex flex-col">
              {/* Encabezado de Tabla */}
              <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <div className="flex-1">Producto</div>
                <div className="w-24 text-right">Precio</div>
                <div className="w-20 text-center">Stock</div>
                <div className="w-12 text-center"></div>
              </div>

              {/* Cuerpo de Tabla Scrollable */}
              <div className="overflow-y-auto flex-grow p-0">
                {productos.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-100">
                    <tbody className="bg-white divide-y divide-gray-50">
                      {productos.slice(0, 100).map((producto) => {
                        const stock = parseInt(producto.stock_total || 0);
                        const minStock = parseInt(producto.stock_minimo || 5);
                        
                        let stockBadge = "bg-green-100 text-green-800";
                        if (stock <= 0) stockBadge = "bg-red-100 text-red-800";
                        else if (stock <= minStock) stockBadge = "bg-orange-100 text-orange-800";

                        const precioMostrar = isMayorista && producto.precio_mayoreo > 0 ? producto.precio_mayoreo : producto.precio_venta;

                        return (
                          <tr 
                            key={producto.id} 
                            onClick={() => agregarAlCarrito(producto)}
                            className={`hover:bg-blue-50 cursor-pointer transition-colors group ${stock <= 0 ? 'opacity-60 bg-gray-50' : ''}`}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center">
                                <div>
                                  <div className="text-sm font-medium text-gray-900 truncate max-w-[200px] sm:max-w-xs lg:max-w-md">
                                    {producto.nombre}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {producto.codigo_barras}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <div className="text-sm font-bold text-primary-600">
                                {formatCurrency(precioMostrar)}
                              </div>
                              {isMayorista && (
                                <span className="text-[10px] inline-flex items-center px-1.5 py-0.5 rounded font-medium bg-yellow-100 text-yellow-800">
                                  MAY
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${stockBadge}`}>
                                {stock}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  agregarAlCarrito(producto);
                                }}
                                className="text-gray-400 hover:text-primary-600 transition-colors p-1 rounded-full hover:bg-primary-50"
                              >
                                <Plus className="h-5 w-5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                    <Search className="h-12 w-12 mb-2 opacity-20" />
                    <p>No hay productos para mostrar</p>
                  </div>
                )}
              </div>
              
              {/* Footer de lista (Contador) */}
              <div className="bg-gray-50 border-t border-gray-200 px-4 py-2 text-xs text-gray-500 flex justify-between items-center">
                <span>Mostrando {Math.min(100, productos.length)} de {productos.length} productos</span>
                {productos.length > 100 && (
                  <span className="text-orange-600">Use el buscador para ver más</span>
                )}
              </div>
            </div>
          </div>

          {/* Carrito - Pantalla Completa */}
          <div className="flex flex-col h-[calc(100vh-200px)]">
          {/* Selector de Cliente - Compacto en móvil */}
          {/* Cliente seleccionado (solo si hay uno) */}
          {cliente && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-primary-600" />
                  <p className="font-medium text-sm">{cliente.nombre}</p>
                  {cliente.telefono && <span className="text-xs text-gray-500">• {cliente.telefono}</span>}
                </div>
                <button
                  onClick={() => {
                    setCliente(null);
                    setBusquedaCliente('');
                  }}
                  className="text-red-500 hover:bg-red-50 p-1 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Carrito Grande */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 flex-grow overflow-hidden flex flex-col">
            {/* Header del Carrito */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-6 h-6 text-white" />
                  <h2 className="text-xl font-bold text-white">Carrito de Compras</h2>
                </div>
                <span className="bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  {carrito.length} productos
                </span>
              </div>
            </div>

            {/* Items del Carrito - Scrollable */}
            <div className="flex-grow overflow-y-auto p-4">
              {carrito.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <ShoppingCart className="w-20 h-20 mb-4 opacity-20" />
                  <p className="text-lg">Carrito vacío</p>
                  <p className="text-sm mt-2">Agrega productos para comenzar</p>
                </div>
              ) : (
                <div className="space-y-3">
              {carrito.map((item) => (
                <div key={item.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary-300 transition-colors">
                  <div className="flex gap-2 mb-2">
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <p className="font-medium text-sm text-gray-900">{item.nombre}</p>
                        {item.precio_mayoreo > 0 && (
                          <button
                            onClick={() => toggleMayoreoItem(item.id)}
                            className={`ml-2 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${
                              Math.abs(item.precio_unitario - item.precio_mayoreo) < 0.01
                                ? 'bg-yellow-100 text-yellow-800 border-yellow-300' 
                                : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
                            }`}
                            title="Alternar Precio Mayoreo"
                          >
                            {Math.abs(item.precio_unitario - item.precio_mayoreo) < 0.01 ? 'MAYOREO' : 'NORMAL'}
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-600">
                        {formatCurrency(item.precio_unitario)} x {item.cantidad}
                      </p>
                    </div>
                    <button
                      onClick={() => eliminarDelCarrito(item.id)}
                      className="text-red-600 hover:bg-red-100 p-2 rounded h-fit"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-700 block mb-1">Cantidad</label>
                      <div className="flex items-center">
                        <button 
                          className="w-8 h-8 bg-gray-200 rounded-l text-gray-600 hover:bg-gray-300"
                          onClick={() => actualizarCantidad(item.id, item.cantidad - 1)}
                        >-</button>
                        <input
                          type="number"
                          value={item.cantidad}
                          onChange={(e) => actualizarCantidad(item.id, e.target.value)}
                          className="w-full h-8 border-y border-gray-300 text-center text-sm focus:ring-0"
                          min="1"
                          step="0.01"
                        />
                         <button 
                          className="w-8 h-8 bg-gray-200 rounded-r text-gray-600 hover:bg-gray-300"
                          onClick={() => actualizarCantidad(item.id, parseFloat(item.cantidad) + 1)}
                        >+</button>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-700 block mb-1">Descuento</label>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">Q</span>
                        <input
                          type="number"
                          value={item.descuento_monto || ''}
                          onChange={(e) => actualizarDescuento(item.id, e.target.value)}
                          placeholder="0.00"
                          className="w-full pl-6 pr-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:ring-primary-500 focus:border-primary-500"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-2 pt-2 border-t border-gray-300 flex justify-between items-center">
                    <span className="text-xs text-gray-600">Subtotal:</span>
                    <div className="text-right">
                      {item.descuento_monto > 0 && (
                        <div className="text-xs text-gray-500 line-through">
                          {formatCurrency(item.precio_unitario * item.cantidad)}
                        </div>
                      )}
                      <span className="font-bold text-sm text-primary-600">
                        {formatCurrency((item.precio_unitario * item.cantidad) - (item.descuento_monto || 0))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
                </div>
              )}
            </div>

            {/* Totales y Acciones - Fixed Bottom */}
            <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">{formatCurrency(subtotal)}</span>
              </div>
              
              {totalDescuentos > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Descuentos:</span>
                  <span className="font-medium text-red-600">-{formatCurrency(totalDescuentos)}</span>
                </div>
              )}
              
              {config?.usar_impuestos && impuestos > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">IVA ({config.porcentaje_iva}%):</span>
                  <span className="font-medium text-green-600">+{formatCurrency(impuestos)}</span>
                </div>
              )}
              
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total:</span>
                <span className="text-primary-600">{formatCurrency(total)}</span>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="p-4 bg-white border-t grid grid-cols-2 gap-2">
              <button
                onClick={handlePagoContado}
                disabled={carrito.length === 0}
                className="flex items-center justify-center gap-2 bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                tabIndex="2"
              >
                <ShoppingCart className="w-5 h-5" />
                <span className="font-semibold">Cobrar</span>
              </button>
              
              <button
                onClick={() => {
                  if (carrito.length === 0) return;
                  if (cliente) {
                    setShowConfirmarCredito(true);
                  } else {
                    setShowSelectorCliente(true);
                  }
                }}
                disabled={carrito.length === 0}
                className="flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CreditCard className="w-5 h-5" />
                <span className="font-medium">Crédito</span>
              </button>
            </div>
            
            <button
              onClick={() => {
                 if (window.confirm('¿Está seguro de cancelar la venta actual?')) {
                   setCarrito([]);
                   setCliente(null);
                 }
              }}
              disabled={carrito.length === 0}
              className="w-full mt-2 py-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
            >
              Cancelar Venta
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Pago (Contado) */}
      {showPagoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">💰 Cobrar Venta</h2>
              <button
                onClick={() => setShowPagoModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="text-center mb-8">
              <p className="text-gray-500 mb-1">Total a Pagar</p>
              <p className="text-4xl font-bold text-primary-600">{formatCurrency(total)}</p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monto Recibido
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">Q</span>
                  <input
                    type="number"
                    value={montoRecibido}
                    onChange={(e) => {
                      const val = e.target.value;
                      setMontoRecibido(val);
                      setCambio(val ? parseFloat(val) - total : 0);
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && parseFloat(montoRecibido) >= total) {
                        procesarVenta(false);
                      }
                    }}
                    className="w-full pl-10 pr-4 py-4 text-2xl border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center"
                    placeholder="0.00"
                    autoFocus
                  />
                </div>
              </div>

              {parseFloat(montoRecibido) > 0 && (
                <div className={`p-4 rounded-xl text-center ${
                  cambio >= 0 ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'
                }`}>
                  <p className={`text-sm ${cambio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {cambio >= 0 ? 'Cambio a devolver' : 'Faltante'}
                  </p>
                  <p className={`text-2xl font-bold ${cambio >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(Math.abs(cambio))}
                  </p>
                </div>
              )}

              <button
                onClick={() => procesarVenta(false)}
                disabled={!montoRecibido || parseFloat(montoRecibido) < total}
                className="w-full py-4 bg-primary-600 text-white text-lg font-bold rounded-xl hover:bg-primary-700 transition-all transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              >
                Confirmar Pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Selector Cliente */}
      {showSelectorCliente && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Seleccionar Cliente</h2>
            
            <div className="mb-4">
              <input
                type="text"
                value={busquedaCliente}
                onChange={(e) => setBusquedaCliente(e.target.value)}
                placeholder="Buscar cliente..."
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />
            </div>

            <div className="max-h-60 overflow-y-auto border rounded-lg mb-4">
              {clientes.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <p>No hay clientes registrados</p>
                  <button
                    onClick={() => {
                      setShowSelectorCliente(false);
                      setShowNuevoCliente(true);
                    }}
                    className="mt-2 text-primary-600 hover:underline"
                  >
                    + Crear Primer Cliente
                  </button>
                </div>
              ) : (
                clientes
                  .filter(c => {
                    if (!busquedaCliente) return true;
                    return c.nombre?.toLowerCase().includes(busquedaCliente.toLowerCase()) ||
                           c.telefono?.includes(busquedaCliente);
                  })
                  .map(c => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setCliente(c);
                        setBusquedaCliente('');
                        setShowSelectorCliente(false);
                        // Mostrar confirmación en lugar de procesar automáticamente
                        setShowConfirmarCredito(true);
                      }}
                      className="w-full text-left p-3 hover:bg-primary-50 border-b last:border-b-0 transition-colors"
                    >
                      <p className="font-medium">{c.nombre}</p>
                      <p className="text-sm text-gray-600">
                        {c.telefono ? `📱 ${c.telefono}` : c.direccion ? `📍 ${c.direccion.substring(0, 40)}...` : '👤 Cliente registrado'}
                      </p>
                    </button>
                  ))
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowSelectorCliente(false)}
                className="flex-1 btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowSelectorCliente(false);
                  setShowNuevoCliente(true);
                }}
                className="flex-1 btn-primary"
              >
                + Nuevo Cliente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Venta Rápida */}
      {showVentaRapidaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Zap className="w-6 h-6 text-purple-600" />
                Venta Rápida / Manual
              </h2>
              <button
                onClick={() => setShowVentaRapidaModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción del Producto <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={ventaRapidaData.descripcion}
                  onChange={(e) => setVentaRapidaData({...ventaRapidaData, descripcion: e.target.value})}
                  placeholder="Ej: Servicio, Producto Vario..."
                  className="input-field"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Precio <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={ventaRapidaData.precio}
                  onChange={(e) => setVentaRapidaData({...ventaRapidaData, precio: e.target.value})}
                  placeholder="0.00"
                  className="input-field"
                  min="0"
                  step="0.01"
                  onKeyPress={(e) => e.key === 'Enter' && agregarVentaRapida()}
                />
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
                ℹ️ Este artículo se agregará al carrito sin afectar el inventario.
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowVentaRapidaModal(false)}
                className="flex-1 btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={agregarVentaRapida}
                disabled={!ventaRapidaData.descripcion || !ventaRapidaData.precio}
                className="flex-1 bg-purple-600 text-white hover:bg-purple-700 btn-primary"
              >
                Agregar al Carrito
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de crédito */}
      {showConfirmarCredito && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">💳 Confirmar Venta a Crédito</h2>
              <button
                onClick={() => setShowConfirmarCredito(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-medium text-blue-900 mb-2">Cliente seleccionado:</p>
                <p className="text-lg font-bold text-blue-800">{cliente?.nombre}</p>
                {cliente?.telefono && (
                  <p className="text-sm text-blue-600 mt-1">📱 {cliente.telefono}</p>
                )}
              </div>
              
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Productos:</span>
                    <span className="font-medium">{carrito.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">{formatCurrency(subtotal)}</span>
                  </div>
                  {totalDescuentos > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Descuentos:</span>
                      <span className="font-medium text-red-600">-{formatCurrency(totalDescuentos)}</span>
                    </div>
                  )}
                  {impuestos > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">IVA:</span>
                      <span className="font-medium text-green-600">+{formatCurrency(impuestos)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-gray-300">
                    <span className="text-lg font-bold">Total:</span>
                    <span className="text-lg font-bold text-primary-600">{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ Esta venta se registrará como <strong>crédito</strong> a nombre de <strong>{cliente?.nombre}</strong>
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowConfirmarCredito(false)}
                className="flex-1 btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowConfirmarCredito(false);
                  procesarVenta(true);
                }}
                className="flex-1 btn-primary bg-green-600 hover:bg-green-700"
              >
                ✓ Confirmar Crédito
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para crear nuevo cliente */}
      {showNuevoCliente && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900"> Nuevo Cliente</h2>
              <button
                onClick={() => setShowNuevoCliente(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nuevoCliente.nombre}
                  onChange={(e) => setNuevoCliente({...nuevoCliente, nombre: e.target.value})}
                  placeholder="Nombre del cliente"
                  className="input-field"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  type="text"
                  value={nuevoCliente.telefono}
                  onChange={(e) => setNuevoCliente({...nuevoCliente, telefono: e.target.value})}
                  placeholder="1234-5678"
                  className="input-field"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección
                </label>
                <textarea
                  value={nuevoCliente.direccion}
                  onChange={(e) => setNuevoCliente({...nuevoCliente, direccion: e.target.value})}
                  placeholder="Dirección completa"
                  className="input-field"
                  rows="3"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowNuevoCliente(false)}
                className="flex-1 btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={guardarNuevoCliente}
                className="flex-1 btn-primary"
              >
                Guardar Cliente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de opciones de impresión */}
      {showPrintOptions && ultimaVenta && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">🧾 Ticket de Venta</h2>
              <button
                onClick={() => {
                  setShowPrintOptions(false);
                  focusBarcodeInput();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
              <p className="text-green-800 font-medium text-center">
                ✅ Venta completada exitosamente
              </p>
              <p className="text-green-600 text-sm text-center mt-1">
                Folio: {ultimaVenta.folio}
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  const printer = new TicketPrinter({
                    nombreNegocio: config?.nombre_negocio || 'Mi Tienda',
                    direccion: config?.direccion || '',
                    telefono: config?.telefono || '',
                    rfc: config?.rfc || ''
                  });
                  printer.print(ultimaVenta);
                  setShowPrintOptions(false);
                  focusBarcodeInput();
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                <span className="font-semibold">Imprimir Ticket</span>
              </button>
              
              <button
                onClick={() => {
                  const printer = new TicketPrinter({
                    nombreNegocio: config?.nombre_negocio || 'Mi Tienda',
                    direccion: config?.direccion || '',
                    telefono: config?.telefono || '',
                    rfc: config?.rfc || ''
                  });
                  printer.download(ultimaVenta);
                  setShowPrintOptions(false);
                  focusBarcodeInput();
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-semibold">Guardar PDF</span>
              </button>
              
              {cliente?.telefono && (
                <button
                  onClick={() => {
                    compartirWhatsApp();
                    setShowPrintOptions(false);
                    focusBarcodeInput();
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10h5v-2h-5c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8v1.43c0 .79-.71 1.57-1.5 1.57s-1.5-.78-1.5-1.57V12c0-2.76-2.24-5-5-5s-5 2.24-5 5 2.24 5 5 5c1.38 0 2.64-.56 3.54-1.47.65.89 1.77 1.47 2.96 1.47 1.97 0 3.5-1.6 3.5-3.57V12c0-5.52-4.48-10-10-10zm0 13c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
                  </svg>
                  <span className="font-semibold">Enviar por WhatsApp</span>
                </button>
              )}
              
              <button
                onClick={() => {
                  setShowPrintOptions(false);
                  focusBarcodeInput();
                }}
                className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scanner de código de barras */}
      {isScannerOpen && (
        <BarcodeScannerNew
          isOpen={true}
          onDetected={handleBarcodeScan}
          onClose={() => setIsScannerOpen(false)}
        />
      )}
      </div>
    </div>
  );
};

export default VentasPage;
